import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { GuiaEntradaCompra } from '../types/guiaEntradaCompra.types';
import { GuiaEntradaCompraService } from '../services/guiaEntradaCompraService';

interface GuiaEntradaCompraState {
  items: GuiaEntradaCompra[];
  selectedItem: GuiaEntradaCompra | null;
  loading: boolean;
  error: string | null;

  fetchAll: () => Promise<void>;
  fetchById: (id: number) => Promise<void>;
  save: (data: GuiaEntradaCompra) => Promise<void>;
  remove: (id: number) => Promise<void>;
  setSelectedItem: (item: GuiaEntradaCompra | null) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  items: [],
  selectedItem: null,
  loading: false,
  error: null,
};

export const useGuiaEntradaCompraStore = create<GuiaEntradaCompraState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchAll: async () => {
        set({ loading: true, error: null }, false, 'ge/fetchAll/start');
        try {
          const items = await GuiaEntradaCompraService.getAll();
          set({ items, loading: false }, false, 'ge/fetchAll/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar guias' }, false, 'ge/fetchAll/error');
        }
      },

      fetchById: async (id: number) => {
        set({ loading: true, error: null }, false, 'ge/fetchById/start');
        try {
          const item = await GuiaEntradaCompraService.getById(id);
          set({ selectedItem: item, loading: false }, false, 'ge/fetchById/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar guia' }, false, 'ge/fetchById/error');
        }
      },

      save: async (data: GuiaEntradaCompra) => {
        set({ loading: true, error: null }, false, 'ge/save/start');
        try {
          await GuiaEntradaCompraService.save(data);
          set({ loading: false }, false, 'ge/save/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al guardar' }, false, 'ge/save/error');
          throw error;
        }
      },

      remove: async (id: number) => {
        set({ loading: true, error: null }, false, 'ge/delete/start');
        try {
          await GuiaEntradaCompraService.delete(id);
          set((state) => ({
            items: state.items.filter((i) => i.idGuiaEntradaCompra !== id),
            loading: false,
          }), false, 'ge/delete/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al eliminar' }, false, 'ge/delete/error');
        }
      },

      setSelectedItem: (item) => set({ selectedItem: item }, false, 'ge/setSelected'),
      clearError: () => set({ error: null }, false, 'ge/clearError'),
      reset: () => set(initialState, false, 'ge/reset'),
    }),
    { name: 'guia-entrada-compra-store' }
  )
);
